import type { ReactNode } from 'react';

import AcButton from './AcButton';
import {
  clinicDateLabel,
  isClinicDate,
  resolveDayScope,
  type DayScopeKind,
} from './day-scope';

/**
 * DAY-SCOPE-PRIMITIVE-AND-ADOPTION-V1 -- THE DATE COMMAND BAR.
 *
 * The ONE registered day control:  [ Previous ] [ Selected date ] [ Next ]
 * [ Today ].  Every day-driven surface renders THIS component; no surface
 * keeps a local stepper, a local date field, or a second set of day words.
 *
 * REUSE GATE (A1). The registered bars were checked first and none can carry
 * this job:
 *   - SelectorBar (ui/SelectorBar.tsx) is an OPTION-LIST bar -- every control
 *     is a <select> over a caller-supplied vocabulary. A clinic day is an
 *     unbounded ordinal value with prev / next / anchor semantics, which an
 *     option list cannot express.
 *   - FilterBar / SortControl / ViewChips (ui/list/*) are token and column
 *     controls over a list query, not a day axis.
 *   - PageHeader / SummaryHeader are page chrome, not controls.
 * So this is a REGISTERED EXTENSION of the control vocabulary, documented in
 * this PR (registry entry component.date-command-bar) rather than a
 * role-specific copy. It composes the EXISTING canon only: AcButton, the
 * --ac-* token layer, the `.ac-sr-only` label idiom -- zero new tokens, zero
 * local visual language, no new chip tone.
 *
 * PRESENTATION ONLY. This component owns no state, issues no read, and knows
 * nothing about clinics, roles, or PHI. The container owns the selected day
 * (see ./use-day-scope.ts for the URL-state hook) and the fenced read.
 *
 * COPY LAW (A2): "Today", never "Return to current date"; verb-first
 * controls; no explanatory sentence where the control explains itself; the
 * selected date is the loudest thing in the bar; and the Today control
 * reflects when today is ALREADY selected (aria-current + a spent visual
 * state) instead of pretending to be an available move.
 *
 * ACCESSIBILITY: the bar is a labelled group; the date field carries a real
 * <label> (visually hidden, since the value beside it is the visible label);
 * the scope word ("Past day" / "Future day") is TEXT, never colour alone; and
 * nothing here depends on animation.
 *
 * Testability (repo idiom -- react-dom/server, no jsdom): pure props in,
 * markup out.
 */

export const DAY_BAR_PREV_COPY = 'Previous day';
export const DAY_BAR_NEXT_COPY = 'Next day';
export const DAY_BAR_TODAY_COPY = 'Today';
export const DAY_BAR_DATE_LABEL = 'Selected date';
/** The scope words. TEXT, so the bar reads correctly in greyscale. */
export const DAY_BAR_PAST_LABEL = 'Past day';
export const DAY_BAR_FUTURE_LABEL = 'Future day';

export interface DateCommandBarProps {
  /** The selected clinic day, 'YYYY-MM-DD'. */
  readonly date: string;
  /** The clinic's today, 'YYYY-MM-DD' -- injected, never read from a clock
   *  here, so a rendered bar is deterministic and pinnable. */
  readonly today: string;
  /** Pick an arbitrary day. */
  readonly onSelect: (date: string) => void;
  /** Move exactly one clinic day (-1 / +1). */
  readonly onStep: (deltaDays: number) => void;
  /** Jump to the clinic's today. */
  readonly onToday: () => void;
  /** Optional lawful context slot (e.g. the clinic switcher a multi-clinic
   *  operator already has). Rendered after the controls; the bar never mints
   *  a scope control of its own. */
  readonly children?: ReactNode;
  /** Accessible name for the group (defaults to a plain day-navigation name). */
  readonly label?: string;
  /** DOCTOR-COCKPIT-VISUAL-CONFORM-V1 (founder ruling on PR #529, MY DAY
   *  (b)): the candidate-b daybar grammar -- `[<] [Thu 06 Aug 2026] [>]
   *  [Today]` -- as an ADDITIVE presentation of THIS one control.
   *  'compact' renders glyph steppers (full a11y words stay as aria-labels
   *  + sr-only text) and keeps the native date input as the picker beneath
   *  the visible day label. Default 'full' is byte-identical for every
   *  existing adopter. */
  readonly variant?: 'full' | 'compact';
  /** A host with a single-box date contract can keep the Today jump in its
   *  secondary controls while reusing this same picker and steppers. */
  readonly showToday?: boolean;
  readonly showScope?: boolean;
  readonly openPickerOnClick?: boolean;
  readonly testId?: string;
}

export default function DateCommandBar({
  date,
  today,
  onSelect,
  onStep,
  onToday,
  children,
  label = 'Clinic day',
  variant = 'full',
  showToday = true,
  showScope = true,
  openPickerOnClick = false,
  testId = 'ac-daybar',
}: DateCommandBarProps): JSX.Element {
  const scope: DayScopeKind = resolveDayScope(date, today);
  const isToday = scope === 'today';
  const dayLabel = clinicDateLabel(date);
  const inputValue = isClinicDate(date) ? date : '';
  const compact = variant === 'compact';

  return (
    <div
      className={compact ? 'ac-daybar ac-daybar--compact' : 'ac-daybar'}
      role="group"
      aria-label={label}
      data-testid={testId}
      data-day-scope={scope}
    >
      <AcButton
        variant="secondary"
        size="sm"
        onClick={() => onStep(-1)}
        {...(compact ? { 'aria-label': DAY_BAR_PREV_COPY } : {})}
        data-testid={`${testId}-prev`}
      >
        {compact ? (
          <span aria-hidden="true" className="ac-daybar__glyph">
            {'\u2039'}
          </span>
        ) : (
          DAY_BAR_PREV_COPY
        )}
      </AcButton>

      {/* The selected day: the loudest element in the bar. The native date
          input IS the picker (keyboard reachable, no overlay machinery, the
          DaySlotPicker date-field pattern) and rides INVISIBLY under the
          label as the same hit target in every variant (FRONT-DESK-DAY-
          BOARD-V1, the CALANDER row: the date prints ONCE); the human label
          is the one readable truth the operator scans. */}
      <label className="ac-daybar__date">
        <span className="ac-sr-only">{DAY_BAR_DATE_LABEL}</span>
        <input
          type="date"
          className="ac-daybar__input"
          value={inputValue}
          onChange={(e) => onSelect(e.target.value)}
          onClick={openPickerOnClick ? (event) => {
            // Native calendar retains keyboard/date semantics; unsupported hosts keep the input.
            try { event.currentTarget.showPicker?.(); } catch { /* native input remains operable */ }
          } : undefined}
          data-testid={`${testId}-date`}
        />
        <span className="ac-daybar__daylabel" data-testid={`${testId}-daylabel`}>
          {dayLabel === '' ? date : dayLabel}
        </span>
      </label>

      <AcButton
        variant="secondary"
        size="sm"
        onClick={() => onStep(1)}
        {...(compact ? { 'aria-label': DAY_BAR_NEXT_COPY } : {})}
        data-testid={`${testId}-next`}
      >
        {compact ? (
          <span aria-hidden="true" className="ac-daybar__glyph">
            {'\u203A'}
          </span>
        ) : (
          DAY_BAR_NEXT_COPY
        )}
      </AcButton>

      {/* THE TODAY ANCHOR. When today is already selected the control stays
          present and operable (pressing it is a harmless no-op that also
          clears a stale ?date) but says so: aria-current marks it, and the
          spent variant stops it competing with the live moves. The state is
          carried by aria-current (styled by attribute selector), never by a
          className override -- AcButton owns its own class list. */}
      {showToday ? <AcButton
        variant={isToday ? 'ghost' : 'primary'}
        size="sm"
        onClick={onToday}
        {...(isToday ? { 'aria-current': 'date' as const } : {})}
        data-testid={`${testId}-today`}
      >
        {DAY_BAR_TODAY_COPY}
      </AcButton> : null}

      {/* The scope word -- TEXT, never a colour-only signal, and absent on
          today (the copy law forbids narrating the present). */}
      {isToday || !showScope ? null : (
        <span className="ac-daybar__scope" data-testid={`${testId}-scope`}>
          {scope === 'past' ? DAY_BAR_PAST_LABEL : DAY_BAR_FUTURE_LABEL}
        </span>
      )}

      {children}
    </div>
  );
}
